/**
 * SecurityAuditModal — Create security audit
 *
 * Schema fields (from schema-compliance.ts / compliance-audits.ts):
 *  - auditType: internal | external | regulatory | certification
 *  - auditStatus: planned | in_progress | completed | cancelled (default: planned)
 *  - createFromTemplate: boolean flag (not persisted — triggers template population on API)
 *  - plannedDate (NOT periodStart/periodEnd — audit has startDate/completionDate but no create fields)
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SecurityAuditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (auditId: string) => void;
}

interface CreatedAudit {
  id: string;
  auditName: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUDIT_TYPES = [
  { value: "internal", label: "Internal" },
  { value: "external", label: "External" },
  { value: "regulatory", label: "Regulatory" },
  { value: "certification", label: "Certification" },
];

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function getDefaultAuditName(auditType: string): string {
  if (!auditType) return "";
  const year = new Date().getFullYear();
  const labels: Record<string, string> = {
    internal: "Internal Security Audit",
    external: "External Security Audit",
    regulatory: "Regulatory Compliance Audit",
    certification: "Certification Audit",
  };
  return `${labels[auditType] ?? auditType} ${year}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SecurityAuditModal({
  open,
  onOpenChange,
  onSuccess,
}: SecurityAuditModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [auditType, setAuditType] = useState("");
  const [auditName, setAuditName] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [createFromTemplate, setCreateFromTemplate] = useState(true);
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [nameAutoSuggested, setNameAutoSuggested] = useState(true);

  useEffect(() => {
    if (open) {
      setAuditType("");
      setAuditName("");
      setPlannedDate("");
      setCreateFromTemplate(true);
      setExecutiveSummary("");
      setNameAutoSuggested(true);
    }
  }, [open]);

  const handleTypeChange = (value: string) => {
    setAuditType(value);
    if (nameAutoSuggested) {
      setAuditName(getDefaultAuditName(value));
    }
  };

  const handleNameChange = (value: string) => {
    setAuditName(value);
    setNameAutoSuggested(false);
  };

  const isValid = auditType.length > 0 && auditName.trim().length > 0;

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/compliance/security-audits", {
        auditType,
        auditName: auditName.trim(),
        plannedDate: plannedDate || undefined,
        executiveSummary: executiveSummary.trim() || undefined,
        createFromTemplate,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to create audit");
      }
      return response.json() as Promise<CreatedAudit>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/security-audits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/security-dashboard"] });
      toast({ title: "Security audit created successfully" });
      onOpenChange(false);
      onSuccess?.(data.id);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="modal-security-audit-form"
      >
        <DialogHeader>
          <DialogTitle>Create Security Audit</DialogTitle>
          <DialogDescription>
            Create a new security audit. Optionally populate checklist items from a template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Audit Type */}
          <div className="space-y-1.5">
            <Label>
              Audit Type <span className="text-destructive">*</span>
            </Label>
            <Select value={auditType} onValueChange={handleTypeChange}>
              <SelectTrigger data-testid="select-audit-type">
                <SelectValue placeholder="Select audit type" />
              </SelectTrigger>
              <SelectContent>
                {AUDIT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Audit Name */}
          <div className="space-y-1.5">
            <Label htmlFor="audit-name">
              Audit Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="audit-name"
              data-testid="input-audit-name"
              value={auditName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Internal Security Audit 2026"
            />
          </div>

          {/* Planned Date */}
          <div className="space-y-1.5">
            <Label htmlFor="planned-date">Planned Date</Label>
            <Input
              id="planned-date"
              data-testid="input-planned-date"
              type="date"
              value={plannedDate}
              onChange={(e) => setPlannedDate(e.target.value)}
            />
          </div>

          {/* Create from Template */}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="create-from-template"
              data-testid="checkbox-create-from-template"
              checked={createFromTemplate}
              onCheckedChange={(checked) => setCreateFromTemplate(checked === true)}
            />
            <div className="space-y-1">
              <Label htmlFor="create-from-template" className="cursor-pointer font-medium">
                Create from Template
              </Label>
              <p className="text-xs text-muted-foreground">
                Pre-populate audit checklist items from the matching template for this audit type.
              </p>
            </div>
          </div>

          {/* Executive Summary */}
          <div className="space-y-1.5">
            <Label htmlFor="executive-summary">Notes</Label>
            <Textarea
              id="executive-summary"
              data-testid="input-executive-summary"
              value={executiveSummary}
              onChange={(e) => setExecutiveSummary(e.target.value)}
              placeholder="Optional notes or context for this audit..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save-audit"
            onClick={() => createMutation.mutate()}
            disabled={!isValid || createMutation.isPending}
          >
            {createMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {createMutation.isPending ? "Creating..." : "Create Audit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
